/**
 * Customer Metrics Helper
 * 
 * Centralized helper for all customer-related business logic and calculations.
 * This module provides consistent customer metrics throughout the application.
 * 
 * Features:
 * - Customer statistics and counts
 * - Revenue calculations per customer
 * - Customer lifecycle tracking
 * - Performance metrics and analytics
 * 
 * Usage:
 * import customerMetrics from '@/helper/customerMetrics'
 * const { getCustomerCount, getTopCustomers } = customerMetrics()
 */

import store from '@/store'
import moment from 'moment'

export default function customerMetrics() {
  // Get all customers from store
  function getCustomers() {
    return store.getters.customerEntries || []
  }

  // Get all order items from store
  function getOrderItems() {
    return store.getters.orderItemsEntries || []
  }

  // Basic Customer Counts
  function getCustomerCount() {
    return getCustomers().length
  }

  function getOrganizationCustomersCount() {
    return getCustomers().filter(customer => customer.isOrganization === true).length
  }

  function getPrivateCustomersCount() {
    return getCustomers().filter(customer => customer.isOrganization !== true).length
  }

  // Customer Discovery
  function getNewestCustomer() {
    const customers = getCustomers()
    if (customers.length === 0) return null

    return [...customers].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]
  }

  function getCustomerWithMostOrders() {
    const customers = getCustomers()
    if (customers.length === 0) return null

    return customers.reduce((maxCustomer, customer) => {
      const customerOrderCount = customer.orders?.length || 0
      const maxOrderCount = maxCustomer?.orders?.length || 0
      return customerOrderCount > maxOrderCount ? customer : maxCustomer
    }, null)
  }

  // Customer Revenue Analytics
  function getCustomerRevenue(customerId) {
    const orderItems = getOrderItems()
    const orders = store.getters.orderEntries || []
    let totalRevenue = 0

    orderItems.forEach(orderItem => {
      // Find the order that contains this order item
      const order = orders.find(o => 
        o.order_items && o.order_items.some(oi => oi.id === orderItem.id))
      
      // Check if this order item belongs to the customer
      const belongsToCustomer = order?.customer?.id === customerId

      if (belongsToCustomer) {
        const rentStart = moment(orderItem.rentStart)
        const moveOut = orderItem.moveOutDate ? moment(orderItem.moveOutDate) : moment()
        const monthsRented = moveOut.diff(rentStart, 'months', true)
        const revenue = Math.ceil(monthsRented) * (orderItem.price || 0)
        
        totalRevenue += revenue
      }
    })

    return totalRevenue
  }

  // Customer-specific metrics for customer view page
  function getCustomerMonthlyRevenue(customerId) {
    const orderItems = getOrderItems()
    const orders = store.getters.orderEntries || []
    let monthlyRevenue = 0

    orderItems.forEach(orderItem => {
      // Find the order that contains this order item
      const order = orders.find(o => 
        o.order_items && o.order_items.some(oi => oi.id === orderItem.id))
      
      // Check if this order item belongs to the customer
      const belongsToCustomer = order?.customer?.id === customerId

      // Check if order item is active (no moveOutDate)
      if (belongsToCustomer && !orderItem.moveOutDate) {
        monthlyRevenue += orderItem.price || 0
      }
    })

    return monthlyRevenue
  }

  function getCustomerActiveStorageCount(customerId) {
    const orderItems = getOrderItems()
    const orders = store.getters.orderEntries || []
    let activeCount = 0

    orderItems.forEach(orderItem => {
      // Find the order that contains this order item
      const order = orders.find(o => 
        o.order_items && o.order_items.some(oi => oi.id === orderItem.id))
      
      const belongsToCustomer = order?.customer?.id === customerId

      // Active storage: belongs to customer and has no moveOutDate
      if (belongsToCustomer && !orderItem.moveOutDate) {
        activeCount += 1
      }
    })

    return activeCount
  }

  function getCustomerTotalBookings(customerId) {
    const customers = getCustomers()
    const customer = customers.find(c => c.id === customerId)
    return customer?.orders?.length || 0
  }

  function getTopCustomersByRevenue(limit = 5) {
    const customers = getCustomers()
    
    const customersWithRevenue = customers.map(customer => ({
      ...customer,
      totalRevenue: getCustomerRevenue(customer.id)
    }))

    return customersWithRevenue
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, limit)
  }

  function getTopCustomersByOrders(limit = 5) {
    const customers = getCustomers()
    
    return customers
      .filter(customer => customer.orders?.length > 0)
      .sort((a, b) => (b.orders?.length || 0) - (a.orders?.length || 0))
      .slice(0, limit)
  }

  // Customer Lifecycle Tracking
  function getCustomersByRegistrationPeriod(period = 'month') {
    const customers = getCustomers()
    const now = moment()
    let startDate

    switch (period) {
      case 'week':
        startDate = moment().subtract(1, 'week')
        break
      case 'month':
        startDate = moment().subtract(1, 'month')
        break
      case 'quarter':
        startDate = moment().subtract(3, 'months')
        break
      case 'year':
        startDate = moment().subtract(1, 'year')
        break
      default:
        startDate = moment().subtract(1, 'month')
    }

    return customers.filter(customer => {
      const registrationDate = moment(customer.created_at)
      return registrationDate.isBetween(startDate, now, null, '[]')
    })
  }

  function getActiveCustomers() {
    const customers = getCustomers()
    const orderItems = getOrderItems()
    const orders = store.getters.orderEntries || []
    
    return customers.filter(customer => 
      // Customer is active if they have at least one active rental
       orderItems.some(orderItem => {
        // Find the order that contains this order item
        const order = orders.find(o => 
          o.order_items && o.order_items.some(oi => oi.id === orderItem.id))
        
        const belongsToCustomer = order?.customer?.id === customer.id
        const isActive = !orderItem.moveOutDate
        return belongsToCustomer && isActive
      }))
  }

  function getInactiveCustomers() {
    const customers = getCustomers()
    const activeCustomerIds = new Set(getActiveCustomers().map(c => c.id))
    
    return customers.filter(customer => !activeCustomerIds.has(customer.id))
  }

  // Customer Performance Metrics
  function getCustomerRetentionRate() {
    const customers = getCustomers()
    if (customers.length === 0) return 0

    const activeCustomers = getActiveCustomers()
    return Math.round((activeCustomers.length / customers.length) * 100)
  }

  function getAverageCustomerValue() {
    const customers = getCustomers()
    if (customers.length === 0) return 0

    const totalRevenue = customers.reduce((sum, customer) => sum + getCustomerRevenue(customer.id), 0)

    return Math.round(totalRevenue / customers.length)
  }

  function getCustomerGrowthTrend(months = 12) {
    const customers = getCustomers()
    const growthData = {}

    for (let i = months - 1; i >= 0; i -= 1) {
      const date = moment().subtract(i, 'months')
      const monthKey = date.format('YYYY-MM')
      const monthLabel = date.format('MMM', 'de')

      const customersInMonth = customers.filter(customer => {
        const registrationDate = moment(customer.created_at)
        return registrationDate.format('YYYY-MM') === monthKey
      }).length

      growthData[monthLabel] = {
        count: customersInMonth,
        month: monthKey,
        label: monthLabel
      }
    }

    return growthData
  }

  // Customer Segmentation
  function getCustomersByCity() {
    const customers = getCustomers()
    const cityStats = {}

    customers.forEach(customer => {
      const city = customer.city || 'Unbekannt'
      if (!cityStats[city]) {
        cityStats[city] = {
          count: 0,
          customers: []
        }
      }
      cityStats[city].count += 1
      cityStats[city].customers.push(customer)
    })

    return Object.entries(cityStats)
      .map(([city, data]) => ({
        city,
        ...data
      }))
      .sort((a, b) => b.count - a.count)
  }

  function getOrganizationVsPrivateRatio() {
    const total = getCustomerCount()
    const organizations = getOrganizationCustomersCount()
    const privateCustomers = getPrivateCustomersCount()

    return {
      total,
      organizations: {
        count: organizations,
        percentage: total > 0 ? Math.round((organizations / total) * 100) : 0
      },
      private: {
        count: privateCustomers,
        percentage: total > 0 ? Math.round((privateCustomers / total) * 100) : 0
      }
    }
  }

  // Customer Search and Filtering
  function searchCustomers(query) {
    if (!query || query.trim() === '') return getCustomers()

    const searchTerm = query.toLowerCase().trim()
    return getCustomers().filter(customer => {
      const firstName = (customer.firstName || '').toLowerCase()
      const lastName = (customer.lastName || '').toLowerCase()
      const email = (customer.email || '').toLowerCase()
      const city = (customer.city || '').toLowerCase()
      const organization = (customer.organizationName || '').toLowerCase()

      return firstName.includes(searchTerm)
             || lastName.includes(searchTerm)
             || email.includes(searchTerm)
             || city.includes(searchTerm)
             || organization.includes(searchTerm)
             || `${firstName} ${lastName}`.includes(searchTerm)
    })
  }

  function filterCustomersByType(type) {
    const customers = getCustomers()
    
    switch (type) {
      case 'organization':
        return customers.filter(c => c.isOrganization === true)
      case 'private':
        return customers.filter(c => c.isOrganization !== true)
      case 'active':
        return getActiveCustomers()
      case 'inactive':
        return getInactiveCustomers()
      default:
        return customers
    }
  }

  // Export all functions
  return {
    // Basic counts
    getCustomerCount,
    getOrganizationCustomersCount,
    getPrivateCustomersCount,
    
    // Customer discovery
    getNewestCustomer,
    getCustomerWithMostOrders,
    
    // Revenue analytics
    getCustomerRevenue,
    getTopCustomersByRevenue,
    getTopCustomersByOrders,
    
    // Customer-specific view functions
    getCustomerMonthlyRevenue,
    getCustomerActiveStorageCount,
    getCustomerTotalBookings,
    
    // Lifecycle tracking
    getCustomersByRegistrationPeriod,
    getActiveCustomers,
    getInactiveCustomers,
    
    // Performance metrics
    getCustomerRetentionRate,
    getAverageCustomerValue,
    getCustomerGrowthTrend,
    
    // Segmentation
    getCustomersByCity,
    getOrganizationVsPrivateRatio,
    
    // Search and filtering
    searchCustomers,
    filterCustomersByType,
    
    // Raw data access
    getCustomers,
    getOrderItems,
  }
}
